import React, { useState } from 'react';
import { ArrowLeft } from 'lucide-react';
import { Recipe } from '../types/recipe';
import { useSearch } from '../hooks/useSearch';
import RecipeGrid from '../components/recipe/RecipeGrid';
import RecipeFilters from '../components/recipe/RecipeFilters';
import SearchBar from '../components/search/SearchBar';
import ScrollToTopButton from '../components/common/ScrollToTopButton';

type Props = {
  recipes: Recipe[];
  onRecipeSelect: (recipe: Recipe) => void;
  onBack: () => void;
};

export default function AllRecipesPage({ recipes, onRecipeSelect, onBack }: Props) {
  const [selectedCategory, setSelectedCategory] = useState<string>('Tout');
  const [searchQuery, setSearchQuery] = useState('');
  const searchResults = useSearch(recipes, searchQuery);
  
  const handleRecipeSelect = (recipe: Recipe) => {
    // Save scroll position before navigating
    const scrollPosition = window.scrollY;
    sessionStorage.setItem('allRecipesScrollPosition', scrollPosition.toString());
    onRecipeSelect(recipe);
  };

  // Restore scroll position when component mounts
  React.useEffect(() => {
    const savedPosition = sessionStorage.getItem('allRecipesScrollPosition');
    if (savedPosition) {
      setTimeout(() => {
        window.scrollTo(0, parseInt(savedPosition));
        sessionStorage.removeItem('allRecipesScrollPosition');
      }, 0);
    }
  }, []);

  // Use search results if there's a search query, otherwise use all recipes
  const baseRecipes = searchQuery.trim() ? searchResults : recipes;
  
  const filteredRecipes = selectedCategory === 'Tout' 
    ? baseRecipes
    : baseRecipes.filter(recipe => {
        switch (selectedCategory) {
          case 'Déjeuner':
            return recipe.category === 'Entrée' || recipe.category === 'repas';
          case 'Dîner':
            return recipe.category === 'Plat Principal' || recipe.category === 'Plat principal';
          case 'Dessert & Gâteaux':
            return recipe.category === 'Dessert';
          default:
            return true;
        }
      });

  const handleSearchRecipeSelect = (recipe: Recipe) => {
    setSearchQuery(''); // Clear search when selecting a recipe
    handleRecipeSelect(recipe);
  };
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={onBack}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Retour
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-6">Toutes les recettes</h1>
        
        <div className="mb-6">
          <SearchBar 
            recipes={recipes} 
            onRecipeSelect={handleSearchRecipeSelect}
          />
        </div>
        
        <RecipeFilters
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
        />
        
        {searchQuery.trim() && filteredRecipes.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">Aucune recette trouvée</p>
            <p className="text-gray-400 text-sm mt-2">
              Essayez avec d'autres mots-clés
            </p>
          </div>
        ) : (
          <RecipeGrid
            recipes={filteredRecipes}
            onRecipeSelect={handleRecipeSelect}
          />
        )}
      </div>
      <ScrollToTopButton />
    </div>
  );
}